import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Divider, Input, Popconfirm, Popover, Space, Table, Tag } from 'antd'
import { DeleteOutlined, EyeOutlined, SearchOutlined, ProfileOutlined } from '@ant-design/icons'
import moment from 'moment'
import * as Constants from '../../../constants'
import './styles.css'

const ClientsTable = ({ data, onRemoveClient, onShowClients, loading }) => {
    const [searchedColumn, setSearchedColumn] = useState('')
    const [searchText, setSearchText] = useState('')
    let searchInput = useRef(null)

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm()
        setSearchText(selectedKeys[0])
        setSearchedColumn(dataIndex)
    }

    const handleReset = clearFilters => {
        clearFilters()
        setSearchText('')
    }

    const getKeyArea = areas => {
        const keyAreas = []
        Object.keys(Constants.areaWorkOrderMap).forEach(key => {
            areas.forEach(area => {
                if (area === Constants.areaWorkOrderMap[key]) {
                    keyAreas.push(key)
                }
            })
        })
        if (keyAreas.length === 0) {
            return `--`
        } else {
            return <Popover content={<ul>
                {
                    keyAreas.map(keyArea => <li key={keyArea}>{keyArea}: {Constants.areaWorkOrderMap[keyArea]}</li>)
                }
            </ul>}>
                <span>{keyAreas[0]} <ProfileOutlined /></span>
            </Popover>
        }
    }

    const isValueInWorkOrder = (record, value) => {
        if(record.SAT) {
            const workOrderTextFormat = `O/T SAT/${record['year'].toString().substring(2, 4)}`
            return (record['year'].toString().toLowerCase().includes(value.toLowerCase()) || workOrderTextFormat.toString().toLowerCase().includes(value.toLowerCase()))
        } else {
            const workOrderTextFormat = `O/T Nº${record['workOrderNumber']}/${record['year'].toString().substring(2, 4)}`
            return (record['workOrderNumber'].toString().toLowerCase().includes(value.toLowerCase()) || record['year'].toString().toLowerCase().includes(value.toLowerCase()) || workOrderTextFormat.toString().toLowerCase().includes(value.toLowerCase()))
        }
    }

    const isAreasMatched = (areas, value) => {
        let isValue = false
        Object.keys(Constants.areaWorkOrderMap).forEach(initials => {
            areas.forEach(area => {
                if ((Constants.areaWorkOrderMap[initials] === area && initials.toString().toLowerCase().includes(value.toString().toLowerCase()))
                    || area.toString().toLowerCase().includes(value.toLowerCase())) {
                    isValue = true
                }
            })
        })
        return isValue
    }

    const getColumnSearchProps = (dataIndex, name) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={node => {
                        searchInput = node
                    }}
                    placeholder={`Buscar ${name}`}
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ width: 188, marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined/>}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Buscar
                    </Button>
                    <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                        Limpiar
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#279FA3' : undefined }}/>,
        onFilter: (value, record) =>
            record[dataIndex] !== undefined
                ? dataIndex === 'workOrderNumber'
                ? isValueInWorkOrder(record, value)
                : dataIndex === 'areas'
                    ? isAreasMatched(record[dataIndex], value)
                    : record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
                : '',
        onFilterDropdownVisibleChange: visible => {
            if (visible) {
                setTimeout(() => searchInput.select(), 100)
            }
        },
        render: (text, record) =>
            'completionPercentage' === dataIndex ?
                text === 100 ?
                    <span className="workOrderCompletedPercentage">{`${text} %`}</span>
                    :
                    <span>{`${text} %`}</span>
                :
                'workOrderNumber' === dataIndex ?
                    record.SAT ?
                        <span>O/T SAT/{record['year'].toString().substring(2, 4)}</span>
                        :
                        <span>O/T Nº{record['workOrderNumber']}/{record['year'].toString().substring(2, 4)}</span>
                    :
                    'areas' === dataIndex ?
                        <span>{getKeyArea(text)}</span>
                        :
                        <span>{text}</span>
    })

    const getColumnSearchPropsWithIds = (dataIndex, name) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={node => {
                        searchInput = node
                    }}
                    placeholder={`Buscar ${name}`}
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ width: 188, marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined/>}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Buscar
                    </Button>
                    <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                        Limpiar
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#279FA3' : undefined }}/>,
        onFilterDropdownVisibleChange: visible => {
            if (visible) {
                setTimeout(() => searchInput.select(), 100)
            }
        },
    })

    const isWorkOrderOnTime = workOrder => {
        if(workOrder.expectedCompletionDate !== '' && workOrder.effectiveEndDate !== '') {
            const expectedDateLong = moment(`${workOrder.expectedCompletionDate} 00:00 -0300`, 'DD/MM/YYYY HH:mm Z').toDate().getTime()
            const endDateLong = moment(`${workOrder.effectiveEndDate} 00:00 -0300`, 'DD/MM/YYYY HH:mm Z').toDate().getTime()
            if (expectedDateLong >= endDateLong) {
                return 'SI'
            } else {
                return 'NO'
            }
        } else {
            if (workOrder.expectedCompletionDate !== '') {
                const todayDateLong = moment(`${moment().utcOffset('-0300').format('DD/MM/YYYY')} 00:00 -0300`, 'DD/MM/YYYY HH:mm Z').toDate().getTime()
                const expectedDateLong = moment(`${workOrder.expectedCompletionDate} 00:00 -0300`, 'DD/MM/YYYY HH:mm Z').toDate().getTime()
                if (todayDateLong <= expectedDateLong) {
                    return '-'
                } else {
                    return 'NO'
                }
            } else {
                return '-'
            }
        }
    }

    const columns = [
        {
            title: 'Razon Social',
            dataIndex: 'name',
            key: 'name',
            ...getColumnSearchProps('name', 'name'),
        },
        {
            title: 'CUIL/CUIT',
            dataIndex: 'cuil',
            key: 'cuil',
            align: 'center',
            ...getColumnSearchProps('cuil', 'cuil'),
            render: (text, record) => <span>{text ? text : '--'}</span>
        },
        {
            title: 'Tipo',
            dataIndex: 'type',
            key: 'type',
            align: 'center',
            render: (text, record) => <span>{text === false ? 'Público' : text === true ? 'Privado' : '--'}</span>
        },
        {
            title: 'Habilitado',
            dataIndex: 'enabled',
            key: 'enabled',
            align: 'center',
            render: (text, record) => <span>{text === true ? 'SI' : 'NO'}</span>
        },
        {
            title: 'Teléfono',
            dataIndex: 'phone',
            key: 'phone',
            align: 'center',
            ...getColumnSearchProps('phone', 'phone'),
            render: (text, record) => <span>{text ? text : '--'}</span>
        },
        {
            title: 'Correo electronico',
            dataIndex: 'email',
            key: 'email',
            align: 'center',
            ...getColumnSearchProps('email', 'email'),
            render: (text, record) => <span>{text ? text : '--'}</span>
        },
        {
            title: 'Interlocutor',
            dataIndex: 'interlocutor',
            key: 'interlocutor',
            align: 'center',
            render: (text, record) => <span>{text ? text : '--'}</span>
        },
        {
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            align: 'center',
            render: (text, record) =>
                <span>
                    <Button loading={loading} onClick={() => onShowClients(record.id)}>{!loading &&
                    <EyeOutlined style={{ color: 'blue' }}/>}</Button>
                    <Divider type="vertical"/>
                    <Popconfirm disabled={loading} title="Se borrará el cliente, ¿Continuar?"
                                onConfirm={() => onRemoveClient({ id:record.id })}>
                        <Button loading={loading}>{!loading && <DeleteOutlined style={{ color: 'red' }}/>}</Button>
                    </Popconfirm>
                </span>
        }
    ]

    return (
        <Table className="outsourcingTableCont" dataSource={data} columns={columns} rowKey="id" size="small" bordered scroll={{ x: 1300 }}/>
    )
}

ClientsTable.propTypes = {
    data: PropTypes.array,
    loading: PropTypes.bool
}

export default ClientsTable
